import React from "react";
import { formatPropertyType, formatProductCategory } from "utils/helperFunctions";

// Read-only detail view for a package rule — what it matches on, and what it
// recommends when it matches. Two plain sections, no icons: the rule's
// conditions read as a sentence, the recommendation as a priced list.

const describeBedrooms = (rule) => {
  const { minBedrooms: min, maxBedrooms: max } = rule;
  if (min == null && max == null) return "Any";
  if (min != null && max != null)
    return min === max ? `${min}` : `${min}–${max}`;
  if (min != null) return `${min} or more`;
  return `Up to ${max}`;
};

// Group rules are set here; agents and company owners make their own in the
// app, and those win over the group's for that agent or company.
export const describeOwner = (rule) => {
  if (rule.userId) {
    return { kind: "agent", label: `Agent · ${rule.userId.name || rule.userId.email || "Unknown"}` };
  }
  if (rule.companyId) {
    return { kind: "company", label: `Company · ${rule.companyId.name || "Unknown"}` };
  }
  return { kind: "group", label: "Group" };
};

const Field = ({ label, value, muted }) => (
  <div className="py-3 border-b border-mediumgray last:border-0">
    <dt className="text-xs uppercase tracking-wide text-darkgray mb-1">
      {label}
    </dt>
    <dd className={`text-sm ${muted ? "text-darkgray" : ""}`}>{value}</dd>
  </div>
);

const SectionTitle = ({ children, hint }) => (
  <div className="mb-1">
    <h5 className="font-medium">{children}</h5>
    {hint && <p className="text-sm text-darkgray">{hint}</p>}
  </div>
);

// A product / package line: name, then its catalogue detail beneath.
const ItemRow = ({ name, detail }) => (
  <li className="flex items-baseline justify-between gap-4 py-2 border-b border-mediumgray last:border-0">
    <span className="text-sm min-w-0">{name}</span>
    {detail && (
      <span className="text-xs text-darkgray whitespace-nowrap flex-shrink-0">
        {detail}
      </span>
    )}
  </li>
);

const productDetail = (p) =>
  [
    p.category ? formatProductCategory(p.category) : null,
    p.price != null ? `$${p.price}` : null,
    p.contractorCompanyId?.name || (p.category ? "In-house" : null),
  ]
    .filter(Boolean)
    .join("  ·  ");

const packageDetail = (p) =>
  [
    p.price != null ? `$${p.price}` : null,
    p.contractorCompanyId?.name || "In-house",
  ]
    .filter(Boolean)
    .join("  ·  ");

export default function PackageRuleView({ rule }) {
  if (!rule) return null;

  const owner = describeOwner(rule);
  const offices = (rule.offices || []).map((o) => o.name || String(o));
  const products = rule.productIds || [];
  const quantities = rule.quantities || {};
  const packages = rule.packageIds || [];
  const propertyTypes = (rule.propertyTypes || []).map(formatPropertyType);
  const finishes = rule.finishes || [];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h4>{rule.name}</h4>
        <p className="text-sm text-darkgray mt-1">
          {owner.kind !== "group"
            ? `${owner.label}${rule.organizationId?.name ? ` · ${rule.organizationId.name}` : ""}`
            : offices.length
              ? `${offices.length} office${offices.length === 1 ? "" : "s"} · ${offices.join(", ")}`
              : "Every office in the organization"}
        </p>
      </div>

      {/* ── Match on ── */}
      <div>
        <SectionTitle hint="A property must satisfy all of these.">
          Matches
        </SectionTitle>
        <dl>
          <Field label="Bedrooms" value={describeBedrooms(rule)} />
          <Field
            label="Property types"
            value={propertyTypes.length ? propertyTypes.join(", ") : "Any"}
            muted={!propertyTypes.length}
          />
          <Field
            label="Finishes"
            value={finishes.length ? finishes.join(", ") : "Any"}
            muted={!finishes.length}
          />
        </dl>
      </div>

      {/* ── Recommends ── */}
      <div>
        <SectionTitle
          hint="Prices come from the catalogue. Anything the office isn't entitled to is dropped when an agent opens the property."
        >
          Recommends
        </SectionTitle>

        {products.length > 0 && (
          <ul className="mt-2">
            {products.map((p, i) => (
              <ItemRow
                key={p._id || i}
                name={`${p.name || String(p)}${
                  quantities[String(p._id)] > 1 ? ` ×${quantities[String(p._id)]}` : ""
                }`}
                detail={productDetail(p)}
              />
            ))}
          </ul>
        )}

        {packages.length > 0 && (
          <>
            <p className="text-xs uppercase tracking-wide text-darkgray mt-4 mb-1">
              Packages
            </p>
            <ul>
              {packages.map((p, i) => (
                <ItemRow
                  key={p._id || i}
                  name={p.name || String(p)}
                  detail={packageDetail(p)}
                />
              ))}
            </ul>
          </>
        )}

        {!products.length && !packages.length && (
          <p className="text-sm text-darkgray mt-2">Nothing selected.</p>
        )}
      </div>

      {/* Kept for support — copy/pasted into a query when tracing a rule. */}
      <p className="text-xs text-darkgray font-mono pt-2 border-t border-mediumgray">
        {rule._id}
      </p>
    </div>
  );
}
